import { COLORS } from '../../constants/theme';
import { formatEnumLabel } from '../../utils/format';
import { SortableTable, useSortState } from '../ui/SortableTable';
import type { ColumnDef } from '../ui/SortableTable';
import type { StandingEntry } from '../../types/politics';

export type SortKey = 'provinceName' | 'honourTitle' | 'voteWeight' | 'votesReceived';

interface Props {
  standings: StandingEntry[];
  myVotedForProvinceId: string | null;
  myProvinceId: string | null;
  onVote: (targetProvinceId: string) => void;
  onRetract: () => void;
  isVoting: boolean;
}

export const HONOUR_ORDER: Record<string, number> = {
  PEASANT: 0, KNIGHT: 1, LORD: 2, BARON: 3, VISCOUNT: 4, COUNT: 5, MARQUIS: 6, DUKE: 7, PRINCE: 8,
};

/** Single source of truth for "what value does this sort key read off a standing entry" —
 *  shared by StandingsTable's ColumnDefs (desktop) and MobilePoliticsShell's <select> (mobile). */
export function politicsSortAccessor(entry: StandingEntry, key: SortKey): string | number {
  switch (key) {
    case 'provinceName': return entry.provinceName;
    case 'honourTitle': return HONOUR_ORDER[entry.honourTitle] ?? 0;
    case 'voteWeight': return entry.voteWeight;
    case 'votesReceived': return entry.votesReceived;
  }
}

/** Feeds MobilePoliticsShell's sort <select> directly. `dir` is Politics-local — not part of the
 *  shared SortableTable/ColumnDef types — so the mobile default view matches desktop's own
 *  default direction instead of a single global ascending order. */
export const SORT_OPTIONS: Array<{ key: SortKey; label: string; dir: 'asc' | 'desc' }> = [
  { key: 'votesReceived', label: 'Votes', dir: 'desc' },
  { key: 'voteWeight', label: 'Weight (votes)', dir: 'desc' },
  { key: 'honourTitle', label: 'Honour', dir: 'desc' },
  { key: 'provinceName', label: 'Province', dir: 'asc' },
];

export function StandingsTable({ standings, myVotedForProvinceId, myProvinceId, onVote, onRetract, isVoting }: Props) {
  const { sortState, handleSort } = useSortState('votesReceived', 'desc');

  const votedForName = (votedForProvinceId: string | null) =>
    votedForProvinceId
      ? standings.find((s) => s.provinceId === votedForProvinceId)?.provinceName ?? votedForProvinceId
      : null;

  const columns: ColumnDef<StandingEntry>[] = [
    {
      key: 'provinceName',
      header: 'Province',
      sticky: true,
      sortable: true,
      sortAccessor: (row) => politicsSortAccessor(row, 'provinceName'),
      render: (row) => {
        const isMe = row.provinceId === myProvinceId;
        return (
          <span style={{ color: row.isMonarch ? '#d4b85f' : COLORS.text.primary, fontWeight: row.isMonarch ? 600 : 400 }}>
            {row.isMonarch && <span style={{ marginRight: 4 }}>♛</span>}
            {row.provinceName}
            {isMe && <span style={{ marginLeft: 6, fontSize: 10, color: COLORS.text.muted }}>(you)</span>}
          </span>
        );
      },
    },
    {
      key: 'honourTitle',
      header: 'Honour',
      sortable: true,
      sortAccessor: (row) => politicsSortAccessor(row, 'honourTitle'),
      render: (row) => formatEnumLabel(row.honourTitle),
    },
    {
      key: 'voteWeight',
      header: 'Weight (votes)',
      sortable: true,
      sortAccessor: (row) => politicsSortAccessor(row, 'voteWeight'),
      render: (row) => row.voteWeight,
    },
    {
      key: 'votesReceived',
      header: 'Votes',
      sortable: true,
      sortAccessor: (row) => politicsSortAccessor(row, 'votesReceived'),
      render: (row) => {
        const isMyVote = row.provinceId === myVotedForProvinceId;
        return (
          <span style={{ fontWeight: isMyVote ? 600 : 400, color: isMyVote ? COLORS.link : COLORS.text.secondary }}>
            {row.votesReceived}
          </span>
        );
      },
    },
    {
      key: 'votedFor',
      header: 'Voted For',
      render: (row) => {
        const isMyVote = row.provinceId === myVotedForProvinceId;
        const name = votedForName(row.votedForProvinceId);
        return (
          <span>
            {name ? name : <span style={{ color: COLORS.text.muted }}>—</span>}
            {isMyVote && <span style={{ marginLeft: 4, fontSize: 10, color: COLORS.link }}>(your vote)</span>}
          </span>
        );
      },
    },
    {
      key: 'action',
      header: 'Action',
      render: (row) =>
        myVotedForProvinceId === row.provinceId ? (
          <button
            onClick={onRetract}
            disabled={isVoting}
            style={{ fontSize: 11, padding: '2px 8px', cursor: isVoting ? 'not-allowed' : 'pointer', opacity: isVoting ? 0.5 : 1, color: COLORS.text.muted, background: 'none', border: `1px solid ${COLORS.text.muted}`, borderRadius: 3 }}
          >
            Retract
          </button>
        ) : (
          <button
            onClick={() => onVote(row.provinceId)}
            disabled={isVoting}
            style={{ fontSize: 11, padding: '2px 8px', cursor: isVoting ? 'not-allowed' : 'pointer', opacity: isVoting ? 0.5 : 1, color: COLORS.link, background: 'none', border: `1px solid ${COLORS.link}`, borderRadius: 3 }}
          >
            Vote
          </button>
        ),
    },
  ];

  return (
    <SortableTable
      columns={columns}
      rows={standings}
      getRowKey={(s) => s.provinceId}
      sortState={sortState}
      onSort={handleSort}
    />
  );
}
